import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, AlertTriangle, PlaneLanding, Loader2, CalendarClock, FolderOpen, ChevronRight,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · Concierge-Property-Statistik + Auslastungs-Widget.
 *
 * Zeigt pro Concierge auf einen Blick:
 *  - Anzahl betreuter Objekte
 *  - Anzahl Objekte aktuell auf ROT (Handlungsbedarf)
 *  - Anzahl Anreisen der laufenden 7-Tage-Fenster
 *  - Offene Buchungen (direkt oder via Property-Fallback zugewiesen)
 *  - Offene Projekte (status ∈ planning/in_progress)
 *
 * Sichtbar für Admins (alle Concierges + Admins) und Concierges (nur eigene
 * Zahlen). Datenquelle: `GET /api/team/concierge-stats` — Backend berechnet
 * die Kennzahlen aus `properties.assigned_concierge_id` + `bookings.assigned_to`
 * + `projects.assigned_to`.
 *
 * Feb 2026 · User-Directive „Concierge-Auslastung Liste":
 *  - Admins UND Concierges tauchen als klickbare Zeilen auf.
 *  - Klick auf den Namen ⇒ Impersonation dieser Person (identisch zum
 *    Concierge-Picker) unter Erhalt der Auslastungs-Infos: der Admin
 *    springt direkt in die persönliche Perspektive dieses Concierges.
 */
export default function ConciergeStatsCard() {
  const { user, setImpersonatedConcierge, setEffectiveRole } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "concierge")) return;
    (async () => {
      try {
        const { data } = await api.get("/team/concierge-stats");
        setRows(data || []);
      } catch (e) { setRows([]); }
    })();
  }, [user]);

  if (!user || (user.role !== "admin" && user.role !== "concierge")) return null;

  // Klick auf eine Zeile → Impersonation dieses Concierges/Admins. Nur für
  // Admins aktiv (echte Concierges sehen ohnehin nur sich selbst).
  const openConciergeDetail = (row) => {
    if (user.role !== "admin") return;
    const targetId = row.concierge?.id;
    if (!targetId) return;
    setEffectiveRole("concierge");
    setImpersonatedConcierge({
      id: targetId,
      name: row.concierge?.name || row.concierge?.email || "",
    });
    nav("/team", { replace: false });
  };

  return (
    <div className="bg-white border border-navy/10 p-6 md:p-7 mb-6" data-testid="concierge-stats-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>Concierge · Auslastung</div>
          <h2 className="font-display text-2xl text-navy">Wer trägt wieviel</h2>
          <p className="text-[13px] text-navy-dark/70 mt-1.5 max-w-xl leading-relaxed">
            {user.role === "admin"
              ? "Objekte, Handlungsbedarf, Anreisen der Woche und aktuelle Auslastung pro Concierge — Klick auf einen Namen öffnet die persönliche Perspektive dieses Concierges."
              : "Ihre zugeteilten Objekte, Anreisen der nächsten 7 Tage und aktuelle Auslastung."}
          </p>
        </div>
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-[13px] text-navy-dark/50 italic py-3">
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.6} /> Lade Statistik …
        </div>
      ) : rows.length === 0 ? (
        <div className="p-6 border border-dashed border-navy/15 text-center text-[13px] text-navy-dark/60">
          Noch kein Concierge im Team eingeloggt bzw. noch keine Objekt-Zuordnung.
          Objekte werden im Kundenprofil pro Objekt zugewiesen.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="concierge-stats-table">
            <thead>
              <tr className="text-left border-b border-navy/10">
                <th className="py-2.5 pr-4 label-caps text-[10.5px] text-navy-dark/60">Concierge</th>
                <th className="py-2.5 px-3 label-caps text-[10.5px] text-navy-dark/60 text-right">Objekte</th>
                <th className="py-2.5 px-3 label-caps text-[10.5px] text-navy-dark/60 text-right">Rot</th>
                <th className="py-2.5 px-3 label-caps text-[10.5px] text-navy-dark/60 text-right">Anreisen · 7T</th>
                <th className="py-2.5 px-3 label-caps text-[10.5px] text-navy-dark/60 text-right">Offene Buchungen</th>
                <th className="py-2.5 px-3 label-caps text-[10.5px] text-navy-dark/60 text-right">Offene Projekte</th>
                {user.role === "admin" && <th className="py-2.5 pl-3 label-caps text-[10.5px] text-navy-dark/60 text-right" aria-label="Detailansicht" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const load = (r.open_bookings || 0) + (r.open_projects || 0);
                // Auslastungs-Ampel: mehr als 8 offene Items = "voll",
                // 4-8 = "solide", <4 = "frei". Ein Admin sieht sofort,
                // wer kapazitätsseitig eher noch was aufnehmen kann.
                const loadColor = load >= 8
                  ? STATUS_COLORS.red
                  : load >= 4
                    ? STATUS_COLORS.blue
                    : STATUS_COLORS.green;
                const isAdminRow = r.concierge?.role === "admin";
                const isSelfRow = r.concierge?.id === user?.id;
                const clickable = user.role === "admin";
                return (
                  <tr
                    key={r.concierge.id}
                    data-testid={`concierge-stats-row-${r.concierge.id}`}
                    data-load={load}
                    data-role={r.concierge?.role || "concierge"}
                    onClick={clickable ? () => openConciergeDetail(r) : undefined}
                    onKeyDown={clickable ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openConciergeDetail(r);
                      }
                    } : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    role={clickable ? "button" : undefined}
                    title={clickable ? `Perspektive von ${r.concierge?.name || r.concierge?.email} übernehmen` : undefined}
                    className={`border-b border-navy/5 last:border-0 ${clickable ? "cursor-pointer hover:bg-navy/[0.02] transition-colors" : ""}`}
                  >
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: withAlpha(BRAND_COLORS.navy, 0.08), color: BRAND_COLORS.navy }}
                        >
                          <Users className="w-4 h-4" strokeWidth={1.6} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-navy leading-tight truncate flex items-center gap-2 flex-wrap">
                            <span
                              className={clickable ? "border-b border-transparent group-hover:border-gold hover:border-gold" : ""}
                              data-testid={`concierge-stats-name-${r.concierge.id}`}
                            >
                              {r.concierge.name || r.concierge.email}
                            </span>
                            {isAdminRow && (
                              <span
                                className="label-caps text-[9px] px-1.5 py-0.5 rounded-full"
                                style={{ background: withAlpha(BRAND_COLORS.gold, 0.18), color: BRAND_COLORS.goldDark }}
                                title="Admin-eigene Concierge-Ebene"
                              >
                                Admin
                              </span>
                            )}
                            {isSelfRow && (
                              <span
                                className="label-caps text-[9px] px-1.5 py-0.5 rounded-full"
                                style={{ background: withAlpha(BRAND_COLORS.navy, 0.10), color: BRAND_COLORS.navy }}
                                title="Sie sind hier eingeloggt"
                              >
                                Sie
                              </span>
                            )}
                          </div>
                          <div className="text-[11.5px] text-navy-dark/55 truncate flex items-center gap-1.5">
                            {r.concierge.email}
                            <span
                              className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                              style={{ background: loadColor }}
                              title={`Auslastung: ${load} offene Items`}
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-right font-display text-lg text-navy tabular-nums">{r.property_count}</td>
                    <td className="py-3 px-3 text-right">
                      <span
                        className="inline-flex items-center gap-1 font-display text-lg tabular-nums"
                        style={r.red_count > 0 ? { color: STATUS_COLORS.red } : { color: BRAND_COLORS.navyDark, opacity: 0.4 }}
                      >
                        {r.red_count > 0 && <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />}
                        {r.red_count}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <span
                        className="inline-flex items-center gap-1 font-display text-lg tabular-nums"
                        style={r.arrivals_next_7_days > 0 ? { color: STATUS_COLORS.blue } : { color: BRAND_COLORS.navyDark, opacity: 0.4 }}
                      >
                        {r.arrivals_next_7_days > 0 && <PlaneLanding className="w-3.5 h-3.5" strokeWidth={1.6} />}
                        {r.arrivals_next_7_days}
                      </span>
                    </td>
                    <td
                      className="py-3 px-3 text-right"
                      data-testid={`concierge-stats-open-bookings-${r.concierge.id}`}
                    >
                      <span
                        className="inline-flex items-center gap-1 font-display text-lg tabular-nums"
                        style={r.open_bookings > 0 ? { color: BRAND_COLORS.navy } : { color: BRAND_COLORS.navyDark, opacity: 0.4 }}
                      >
                        {r.open_bookings > 0 && <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.6} />}
                        {r.open_bookings}
                      </span>
                    </td>
                    <td
                      className="py-3 px-3 text-right"
                      data-testid={`concierge-stats-open-projects-${r.concierge.id}`}
                    >
                      <span
                        className="inline-flex items-center gap-1 font-display text-lg tabular-nums"
                        style={r.open_projects > 0 ? { color: BRAND_COLORS.navy } : { color: BRAND_COLORS.navyDark, opacity: 0.4 }}
                      >
                        {r.open_projects > 0 && <FolderOpen className="w-3.5 h-3.5" strokeWidth={1.6} />}
                        {r.open_projects}
                      </span>
                    </td>
                    {user.role === "admin" && (
                      <td className="py-3 pl-3 text-right text-navy-dark/40" aria-hidden="true">
                        <ChevronRight className="w-4 h-4 inline-block" strokeWidth={1.6} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Ampel-Legende (Admin-only) — hilft neue Admins beim Lesen der
              Auslastungs-Punkte neben den Namen. */}
          {user.role === "admin" && (
            <div className="mt-3 flex items-center gap-4 text-[10.5px] tracking-[0.1em] uppercase text-navy-dark/55">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: STATUS_COLORS.green }} />
                unter 4 Items · frei
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: STATUS_COLORS.blue }} />
                4–7 · solide
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: STATUS_COLORS.red }} />
                ab 8 · voll
              </span>
            </div>
          )}
        </div>
      )}

      {user.role === "admin" && (
        <div className="mt-4 text-[11.5px] text-navy-dark/55">
          Änderungen der Zuordnung erfolgen im Kundenprofil (
          <a href="/team/customers" className="border-b border-gold text-navy-dark hover:text-navy" onClick={(e) => { e.stopPropagation(); }}>
            Kundenverwaltung
          </a>
          ) → jeweiliges Objekt · Bulk-Buchungs-Zuweisung unter{" "}
          <a href="/team/bookings" className="border-b border-gold text-navy-dark hover:text-navy" onClick={(e) => { e.stopPropagation(); }}>
            Buchungen
          </a>.
        </div>
      )}
    </div>
  );
}
